/**
 * The face, wherever a person appears.
 *
 * Lifted out of `account-menu.ts` when the settings page needed the same thing:
 * two avatars built from two code paths drift, and the one place that would
 * show is the one place it matters — the app bar and the account page side by
 * side, disagreeing about who you are.
 */
import { FACE, type FaceKey } from "../../shared/tokens";
import { el } from "./dom";

/**
 * A face colour, stable per person.
 *
 * The six measured face colours are the brand palette, so the account control
 * ends up looking like part of this product rather than a generic auth widget —
 * and it costs no new colour. Hashed from the id, not the display name, so it
 * does not change when someone renames themselves.
 */
const FACE_KEYS: FaceKey[] = ["front", "back", "left", "right", "up", "down"];

export function faceFor(id: string): string {
  let h = 5381;
  for (const ch of id) h = ((h * 33) ^ ch.charCodeAt(0)) >>> 0;
  return FACE[FACE_KEYS[h % FACE_KEYS.length] as FaceKey];
}

export function initial(label: string): string {
  // Intl.Segmenter so an emoji or a combining mark does not get sliced in half.
  const segmenter =
    typeof Intl !== "undefined" && "Segmenter" in Intl
      ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
      : null;
  const first = segmenter ? [...segmenter.segment(label)][0]?.segment : label[0];
  return (first ?? "?").toUpperCase();
}

export interface AvatarInput {
  /** The provider's picture, when there is one. */
  image?: string | null;
  /** Hashed for the fallback colour. Stable across renames. */
  id: string;
  /** Display name, for the initial. */
  label: string;
}

/**
 * An image when the provider gave us one, a coloured initial when it did not.
 *
 * `className` is the caller's, because the app bar's avatar is a 28px control
 * and the settings page's is a 48px portrait — same identity, different size,
 * and neither should inherit the other's box.
 */
/** The coloured initial. Also what a failed image falls back to. */
function initialBadge(user: AvatarInput, className: string): HTMLElement {
  const badge = el("span", {
    class: `${className} ${className}--initial`,
    text: initial(user.label),
  });
  badge.style.background = faceFor(user.id);
  return badge;
}

export function avatar(user: AvatarInput, className: string): HTMLElement {
  if (user.image) {
    const img = Object.assign(document.createElement("img"), {
      src: user.image,
      alt: "",
      className,
      // The provider's CDN, not ours; a broken avatar must not break the
      // control, and referrers do not need to leak our paths.
      referrerPolicy: "no-referrer",
      // Decorative and off the critical path — the name beside it already
      // identifies the account.
      loading: "lazy",
    });

    /*
     * Swap in the initial if the image never arrives.
     *
     * There was no handler here, so a picture that failed left the browser's
     * broken-image glyph in the app bar — which is exactly what happened when
     * the CSP shipped without the avatar hosts in img-src: every Google user
     * saw a torn-page icon where their face should be. A CDN outage, a deleted
     * avatar or an offline tab all produce the same event, so this is worth
     * having even now that the policy allows the host.
     */
    img.addEventListener(
      "error",
      () => {
        img.replaceWith(initialBadge(user, className));
      },
      { once: true },
    );

    return img;
  }

  return initialBadge(user, className);
}
